import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePdf, Image as ImageIcon, File as FileIcon, DownloadSimple, UploadSimple, Spinner, Camera, Eye } from "@phosphor-icons/react";
import { fmtDate, portalErr } from "@/lib/portalApi";

const KAT = { gutachten: "Gutachten", rechnung: "Rechnung", polizei: "Polizeibericht / Unfallbericht", schriftverkehr: "Schreiben der Versicherung", bussgeld: "Bußgeldbescheid", vollmacht: "Vollmacht", abtretung: "Abtretung", sonstige: "Sonstiges / Fotos" };
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

function DocIcon({ ct, name }) {
  const isPdf = ct === "application/pdf" || /\.pdf$/i.test(name || "");
  const isImg = (ct || "").startsWith("image/");
  const Icon = isPdf ? FilePdf : isImg ? ImageIcon : FileIcon;
  return <Icon size={26} weight="duotone" className={isPdf ? "text-[#D32F2F]" : isImg ? "text-[#0EA5E9]" : "text-[#6B7280]"} />;
}

export default function PortalDokumente({ api, onChanged }) {
  const [docs, setDocs] = useState(null);
  const [kat, setKat] = useState("sonstige");
  const [notiz, setNotiz] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const camRef = useRef();

  const load = async () => {
    try { const r = await api.get("/portal/dokumente"); setDocs(r.data); }
    catch (e) { toast.error(portalErr(e, "Dokumente konnten nicht geladen werden")); setDocs([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const open = async (d, download = false) => {
    try {
      const r = await api.get(`/portal/dokumente/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (download) {
        const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(portalErr(e, "Dokument konnte nicht geöffnet werden")); }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f); fd.append("kategorie", kat); fd.append("notiz", notiz);
      try { await api.post("/portal/dokumente/upload", fd); ok++; }
      catch (e) { toast.error(portalErr(e, `„${f.name}“ konnte nicht hochgeladen werden`)); }
    }
    setUploading(false);
    if (ok) { toast.success(ok === 1 ? "Dokument an Ihre Kanzlei übermittelt" : `${ok} Dokumente übermittelt`); setNotiz(""); load(); onChanged?.(); }
  };

  const eigene = (docs || []).filter((d) => d.source === "portal" && !["vollmacht", "abtretung"].includes(d.kategorie));
  const kanzlei = (docs || []).filter((d) => !eigene.includes(d));

  return (
    <div className="space-y-6" data-testid="portal-dokumente">
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF]">Unterlagen</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-1">Dokumente</h1>
      </div>

      {/* Upload */}
      <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-7 space-y-4" data-testid="portal-upload-box">
        <div>
          <h2 className="font-serif text-2xl">Unterlagen an die Kanzlei senden</h2>
          <p className="text-sm text-[#6B7280] mt-1">Fotos vom Unfall, Rechnungen, Schreiben der Versicherung — einfach fotografieren oder als PDF hochladen.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select value={kat} onValueChange={setKat}>
            <SelectTrigger className="h-11" data-testid="portal-upload-kategorie"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["sonstige", "gutachten", "rechnung", "polizei", "schriftverkehr", "bussgeld"].map((k) => <SelectItem key={k} value={k}>{KAT[k]}</SelectItem>)}
            </SelectContent>
          </Select>
          <input value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Kurze Notiz (optional)" className="h-11 rounded-md border border-[#E5E7EB] px-3 text-sm" data-testid="portal-upload-notiz" />
        </div>
        <input ref={fileRef} type="file" multiple accept="application/pdf,image/*" className="hidden" data-testid="portal-file-input" onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="portal-camera-input" onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" className="h-12 rounded-xl" disabled={uploading} onClick={() => camRef.current?.click()} data-testid="portal-camera-btn">
            <Camera size={18} className="mr-2" weight="duotone" /> Foto aufnehmen
          </Button>
          <Button className="h-12 rounded-xl bg-[#1A2238] hover:bg-[#2C3859]" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="portal-upload-btn">
            {uploading ? <Spinner size={18} className="mr-2 animate-spin" /> : <UploadSimple size={18} className="mr-2" />} Datei wählen
          </Button>
        </div>
      </section>

      <DocList titel="Von Ihrer Kanzlei freigegeben" docs={kanzlei} loading={docs === null} onOpen={open} empty="Ihre Kanzlei hat noch keine Dokumente für Sie freigegeben." testId="portal-docs-kanzlei" />
      {eigene.length > 0 && <DocList titel="Von Ihnen hochgeladen" docs={eigene} onOpen={open} testId="portal-docs-eigene" />}
    </div>
  );
}

function DocList({ titel, docs, loading, onOpen, empty, testId }) {
  return (
    <section className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] px-5 pt-5 pb-3">{titel}</p>
      {loading ? (
        <p className="px-5 pb-5 text-sm text-[#6B7280]">Lädt …</p>
      ) : docs.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-[#6B7280]" data-testid={`${testId}-empty`}>{empty}</p>
      ) : (
        <ul className="divide-y divide-[#F3F4F6]">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-5 py-3" data-testid={`portal-doc-${d.id}`}>
              <DocIcon ct={d.content_type} name={d.original_filename} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{d.original_filename}</p>
                <p className="text-xs text-[#6B7280]">{KAT[d.kategorie] || d.kategorie} · {fmtDate(d.created_at)} · {fmtSize(d.size)}</p>
              </div>
              <button onClick={() => onOpen(d)} className="h-9 w-9 rounded-full hover:bg-[#F3F4F6] flex items-center justify-center text-[#1A2238]" title="Ansehen" data-testid={`portal-doc-open-${d.id}`}><Eye size={18} /></button>
              <button onClick={() => onOpen(d, true)} className="h-9 w-9 rounded-full hover:bg-[#F3F4F6] flex items-center justify-center text-[#1A2238]" title="Herunterladen" data-testid={`portal-doc-download-${d.id}`}><DownloadSimple size={18} /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
